import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Lock, Layers, FolderTree, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * Admin taxonomy editor — CRUD for Ronin Box modes + categories.
 *
 *   - Built-in items (custom/shadow/bag/defense, punch/defense/footwork/feint/atmosphere)
 *     can be RENAMED but not deleted.
 *   - New modes/categories appear automatically in the user-facing /ronin-box screen
 *     and the cue picker; their weight rows are seeded to 1 across the board.
 *   - Deleting a mode/category is rejected if any cue still uses it.
 */
const ICON_CHOICES = [
  "Zap", "Target", "Shield", "Sliders", "Sparkles", "Heart", "Timer", "Smartphone",
  "Footprints", "Megaphone", "Activity", "Tag", "Bolt", "Star", "Crosshair", "Layers",
];

export default function RoninTaxonomy() {
  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="ronin-taxonomy-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/ronin-box" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white font-bold mb-2 inline-flex items-center gap-1" data-testid="taxonomy-back">
            <ArrowLeft className="h-3 w-3" /> Back to Ronin Box
          </Link>
          <h1 className="font-display uppercase text-4xl text-white tracking-tight">Modes & categories</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            Add custom workout modes and cue categories. Built-ins can&apos;t be deleted (they protect existing cues), but you can rename them or change their icon and tagline.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <TaxonomyPanel
          title="Modes"
          icon={Layers}
          endpoint="modes"
          testid="modes-panel"
          createHint="A new mode appears as a tile on /ronin-box. Its category weights default to 1 across the board."
          extraFields={["description"]}
          hasVisibility
        />
        <TaxonomyPanel
          title="Categories"
          icon={FolderTree}
          endpoint="categories"
          testid="categories-panel"
          createHint="A new category gets a row in the weight matrix for every mode."
        />
      </div>
    </div>
  );
}

function TaxonomyPanel({ title, icon: Icon, endpoint, testid, createHint, extraFields = [], hasVisibility = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | item
  const [togglingId, setTogglingId] = useState(null);
  const showDescription = extraFields.includes("description");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ronin-box/${endpoint}`);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (item) => {
    setTogglingId(item.id);
    try {
      await api.patch(`/admin/ronin-box/${endpoint}/${item.id}`, { hidden: !item.hidden });
      toast.success(`${item.label} is now ${item.hidden ? "visible" : "hidden"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setTogglingId(null); }
  };

  const onDelete = async (item) => {
    if (item.builtin) {
      toast.error("Built-in items can't be deleted.");
      return;
    }
    if (!window.confirm(`Delete "${item.label}"? Cues using it must be reassigned first.`)) return;
    try {
      await api.delete(`/admin/ronin-box/${endpoint}/${item.id}`);
      toast.success(`Deleted ${item.label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#FF331F]" />
          <h2 className="font-display uppercase text-lg text-white tracking-tight">{title}</h2>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{items.length}</span>
        </div>
        <Button
          size="sm"
          onClick={() => setEditing({})}
          className="bg-[#FF331F] hover:bg-[#FF5242] text-white text-[10px] uppercase tracking-widest font-bold"
          data-testid={`${testid}-create`}
        >
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="p-6 text-zinc-500 flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="p-6 text-zinc-500 text-sm">No items yet.</p>
      ) : (
        <ul className="divide-y divide-[#1a1a1a]">
          {items.map((it) => (
            <li key={it.id} className="px-4 py-3 flex items-center gap-3" data-testid={`${testid}-row-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-base text-white uppercase tracking-tight">{it.label}</span>
                  <span className="font-mono text-[10px] text-zinc-600">{it.id}</span>
                  {it.builtin && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                      <Lock className="h-3 w-3" /> Built-in
                    </span>
                  )}
                  {hasVisibility && it.hidden && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-400" data-testid={`${testid}-hidden-badge-${it.id}`}>
                      <EyeOff className="h-3 w-3" /> Hidden
                    </span>
                  )}
                </div>
                {it.tagline && <p className="text-xs text-zinc-500 mt-0.5">{it.tagline}</p>}
              </div>
              {hasVisibility && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => toggleHidden(it)}
                  disabled={togglingId === it.id}
                  className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white text-[10px] uppercase tracking-widest font-bold"
                  data-testid={`${testid}-toggle-${it.id}`}
                  title={it.hidden ? "Show on app & site" : "Hide from app & site"}
                >
                  {togglingId === it.id
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : it.hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
              )}
              <Button
                variant="outline" size="sm"
                onClick={() => setEditing(it)}
                className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white text-[10px] uppercase tracking-widest font-bold"
                data-testid={`${testid}-edit-${it.id}`}
              >
                <Pencil className="h-3 w-3" />
              </Button>
              {!it.builtin && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => onDelete(it)}
                  className="border-red-900/40 bg-[#050505] text-red-400 hover:bg-red-900/20 hover:text-red-300 text-[10px] uppercase tracking-widest font-bold"
                  data-testid={`${testid}-delete-${it.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <TaxonomyEditor
          item={editing}
          endpoint={endpoint}
          testid={testid}
          showDescription={showDescription}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          createHint={createHint}
        />
      )}
    </div>
  );
}

function TaxonomyEditor({ item, endpoint, testid, showDescription, onClose, onSaved, createHint }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    id:          item.id || "",
    label:       item.label || "",
    tagline:     item.tagline || "",
    icon:        item.icon || "Tag",
    description: item.description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.label.trim()) { toast.error("Label required"); return; }
    if (isNew && !/^[a-z0-9_-]+$/.test(form.id || "")) {
      toast.error("ID must be lowercase a-z, 0-9, _ or - (e.g. footwork_drills)");
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        await api.post(`/admin/ronin-box/${endpoint}`, form);
        toast.success(`Created ${form.label}`);
      } else {
        // Only send mutable fields (server rejects id change on update)
        const payload = {
          label: form.label, tagline: form.tagline, icon: form.icon,
          ...(showDescription ? { description: form.description } : {}),
        };
        await api.patch(`/admin/ronin-box/${endpoint}/${item.id}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-md" data-testid={`${testid}-editor`}>
        <DialogHeader>
          <DialogTitle className="text-white uppercase tracking-tight">
            {isNew ? "New" : `Edit · ${item.label}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {isNew && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">ID</Label>
              <Input
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                placeholder="footwork_drills"
                className="bg-[#050505] border-[#27272A] text-white rounded-sm font-mono"
                data-testid={`${testid}-editor-id`}
              />
              <p className="text-[10px] text-zinc-600 mt-1">Permanent. Lowercase a-z / 0-9 / _ / -.</p>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Label</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Footwork Drills"
              className="bg-[#050505] border-[#27272A] text-white rounded-sm"
              data-testid={`${testid}-editor-label`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Tagline</Label>
            <Input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="Pure movement"
              className="bg-[#050505] border-[#27272A] text-white rounded-sm"
              data-testid={`${testid}-editor-tagline`}
            />
            <p className="text-[10px] text-zinc-600 mt-1">One-line description shown on the tile / category header.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Icon</Label>
            <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
              <SelectTrigger className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid={`${testid}-editor-icon`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                {ICON_CHOICES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {showDescription && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="bg-[#050505] border-[#27272A] text-white rounded-sm"
                data-testid={`${testid}-editor-description`}
              />
              <p className="text-[10px] text-zinc-600 mt-1">Long form — shown in the body of the mode tile.</p>
            </div>
          )}
          {isNew && createHint && (
            <p className="text-[11px] text-zinc-500 bg-[#050505] border border-[#27272A] rounded-sm p-3">{createHint}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px]">
              Cancel
            </Button>
            <Button onClick={save} disabled={busy} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-[10px]" data-testid={`${testid}-editor-save`}>
              {busy ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : null}
              {isNew ? "Create" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
